import { useEffect, useState } from "react";
import Dialog from "../../atom/Dialog";
import { useTranslation } from "react-i18next";
import DialogFooterContainer from "../../atom/Dialog/DialogFooterContainer";
import { Button } from "../../atom/Button";
import Typo from "../../atom/Typo";
import { companyApi } from "@/src/store/apis/corporate/company";
import { isUndefined } from "@/src/utils/is";
import styled from "styled-components";
import Radio from "@/src/components/atom/Radio";
import Loader from "../../atom/Loader";
import colorSet from "@/src/styles/color";
import {
  QDialogRoot,
  QDialogPortal,
  QDialogOverlay,
  QDialogContent,
} from "../../quantum/DialogQuantum";
import zIndex from "@/src/styles/zIndex";

interface SignatureSelectDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onSave?: (selectedSignatureId: number) => void;
  onRegisterClick?: () => void;
  isLoading?: boolean;
}

const SignatureSelectDialog = ({
  open,
  onOpenChange,
  isLoading,
  onSave,
  onRegisterClick,
}: SignatureSelectDialogProps) => {
  const { t } = useTranslation();
  const [selectedSignatureId, setSelectedSignatureId] = useState<number | null>(
    null,
  );

  const { simpleExporterSignatureMedias } =
    companyApi.endpoints.getExporters.useQueryState(undefined, {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isUndefined(currentData) || isFetching;
        const isStable = !isUnstable;

        return {
          simpleExporterSignatureMedias: isStable
            ? currentData.signatureManagement?.simpleExporterSignatureMedias || []
            : [],
        };
      },
    });

  useEffect(() => {
    const defaultSignature = simpleExporterSignatureMedias?.find(
      (item) => item.isDefault,
    );
    if (defaultSignature) {
      setSelectedSignatureId(defaultSignature.signatureSimpleMedia.id);
    }
  }, [simpleExporterSignatureMedias]);

  const hasSignatures = simpleExporterSignatureMedias && simpleExporterSignatureMedias.length > 0;

  // 서명이 없을 때 커스텀 모달 렌더링
  if (!hasSignatures) {
    return (
      <QDialogRoot open={open} onOpenChange={onOpenChange}>
        <QDialogPortal>
          <StyledOverlay />
          <StyledDialogContent>
            <EmptyStateContainer>
              <TitleText>
                {t("common:noRegisteredSignatureTitle")}
              </TitleText>
              <DescriptionText>
                {t("common:noRegisteredSignatureDescription")}
              </DescriptionText>
            </EmptyStateContainer>
            <DialogFooter>
              <Button
                buttonGrade="tertiary"
                buttonColor="black"
                onClick={() => onOpenChange(false)}
              >
                <Typo typoType="btn3m">{t("common:close")}</Typo>
              </Button>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  onRegisterClick?.();
                }}
              >
                <Typo typoType="btn3m" color="white">
                  {t("common:signatureRegister")}
                </Typo>
              </Button>
            </DialogFooter>
          </StyledDialogContent>
        </QDialogPortal>
      </QDialogRoot>
    );
  }

  // 서명이 있을 때 기존 Dialog 사용
  return (
    <Dialog
      title={t("common:signatureSelect")}
      open={open}
      width={624}
      onOpenChange={onOpenChange}
      footer={
        <DialogFooterContainer>
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={() => onOpenChange(false)}
          >
            <Typo typoType="btn3m">{t("common:close")}</Typo>
          </Button>
          <Button
            isLoading={isLoading}
            onClick={() => onSave?.(selectedSignatureId as number)}
          >
            <Typo typoType="btn3m" color="white">
              {t("common:select")}
            </Typo>
          </Button>
        </DialogFooterContainer>
      }
    >
      <Content $isSingle={simpleExporterSignatureMedias?.length === 1}>
        {(simpleExporterSignatureMedias || []).map((item, index) => {
            if (isLoading)
              return (
                <FlexCenter key={index.toString()}>
                  <Loader />
                </FlexCenter>
              );
            return (
              <SingleImageContainer key={index.toString()}>
                <SignatureImage
                  src={item.signatureSimpleMedia.mediaUrl}
                  onClick={() =>
                    setSelectedSignatureId(item.signatureSimpleMedia.id)
                  }
                />
                <RadioRow>
                  <Radio
                    disabled={!item}
                    checked={selectedSignatureId === item.signatureSimpleMedia.id}
                    onClick={() =>
                      setSelectedSignatureId(item.signatureSimpleMedia.id)
                    }
                  >
                    <Typo typoType="b7r" color={item ? "gray2" : "gray7"}>
                      {t(
                        "companyManagement:exporter.companyInfo.signatureManagement.signatureItem",
                      )}{" "}
                      {index + 1}
                    </Typo>
                  </Radio>
                </RadioRow>
              </SingleImageContainer>
            );
          })}
        </Content>
    </Dialog>
  );
};

export default SignatureSelectDialog;

const StyledOverlay = styled(QDialogOverlay)`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${zIndex.dialog};
`;

const StyledDialogContent = styled(QDialogContent)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 16px;
  width: 305px;
  min-height: 158px;
  box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.12);
  z-index: ${zIndex.dialog};
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
`;

const FlexCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 1;
`;

const TitleText = styled.div`
  color: #222729;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
`;

const DescriptionText = styled.div`
  margin-top: 4px;
  color: #5e696e;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const DialogFooter = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
  
  & > button {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    
    /* 닫기 버튼 스타일 */
    &:first-child {
      border-radius: 8px;
      background: #FFF;
      border: none;
      /* Btn Shadow/Black/Default */
      box-shadow: 0px 0px 0px 0.5px rgba(160, 164, 171, 0.20), 0px 1px 2px 0px rgba(5, 29, 57, 0.10);
      
      &:focus {
        outline: none;
      }
    }
  }
`;

const RadioRow = styled.div`
  text-align: center;
  padding-top: 8px;
`;

const SingleImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Content = styled.div<{ $isSingle?: boolean }>`
  display: flex;
  gap: 16px;
  justify-content: ${({ $isSingle }) => ($isSingle ? 'center' : 'flex-start')};
`;

const SignatureImage = styled.img`
  width: 280px;
  height: 128px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid ${colorSet.gray10};
  cursor: pointer;
`;
